/** What decision graphs read from the release: each node's rule data, and document names for definedIn/usedIn. */

/** Release detail that only debug, non-minimal graphs include (they carry each node's rule data). */
export type DecisionGraphRelease = {
  /** The release rule graph node data by guid. */
  ruleNode: (guid: string) => Record<string, any> | undefined;
  /** The release's document tree, for definedIn/usedIn. */
  documents?: Record<string, any>;
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// documents.rs is_document_id
const isDocumentId = (id: string) => id.length > 0 && (UUID.test(id) || (id.length === 28 && id.startsWith("00")));

// documents.rs sanitize_document_name
const sanitizeDocumentName = (name: string) => {
  let result = (name.startsWith("/") ? name : `/${name}`).trim();
  if (result.endsWith(".docx")) result = result.split(".docx")[0];
  if (result.endsWith(".xlsx")) result = result.split(".xlsx")[0];
  return result.replaceAll("\t", " ").trim();
};

type Document = { reference: string; name: string };
const isDocument = (value: any): value is Document =>
  typeof value?.reference === "string" && typeof value?.name === "string";

const findDocument = (documents: Record<string, any>, match: (doc: Document) => boolean): Document | undefined => {
  for (const tree of Object.values(documents ?? {})) {
    if (isDocument(tree)) {
      if (match(tree)) return tree;
    } else if (tree && typeof tree === "object" && !Array.isArray(tree)) {
      const found = findDocument(tree, match);
      if (found) return found;
    }
  }
  return undefined;
};

const findDocumentByName = (documents: Record<string, any>, name: string) => {
  const sanitized = sanitizeDocumentName(name);
  return findDocument(documents, (doc) => sanitizeDocumentName(doc.name) === sanitized);
};

const findDocumentByReference = (documents: Record<string, any>, reference: string) =>
  findDocument(documents, (doc) => doc.reference === reference);

export const documentRef = (documents: Record<string, any>, item: string, asGuids: boolean): string | undefined => {
  if (asGuids) return isDocumentId(item) ? item : findDocumentByName(documents, item)?.reference;
  return isDocumentId(item) ? findDocumentByReference(documents, item)?.name : item;
};

const RELEASE_RULE_NODES = Symbol("releaseRuleNodes");

/** The release detail a legacy graph needs, from a release (raw or parsed rule graph). */
export const decisionGraphRelease = (release: { rule_graph?: any; documents?: any } | undefined): DecisionGraphRelease | undefined => {
  const ruleGraph = release?.rule_graph;
  if (!ruleGraph) return undefined;
  if (typeof ruleGraph.ref === "function") {
    // a DecisionGraph (as releaseWithWorkingGraph gives it): node() takes an id
    return {
      ruleNode: (guid) => {
        const id = ruleGraph.ref(guid);
        return id === undefined ? undefined : ruleGraph.node(id);
      },
      documents: release.documents,
    };
  }
  if (typeof ruleGraph.node === "function") {
    // a graphlib graph, keyed by guid
    return { ruleNode: (guid) => ruleGraph.node(guid), documents: release.documents };
  }
  if (!Array.isArray(ruleGraph.nodes)) return undefined;
  let nodes: Map<string, any> = ruleGraph[RELEASE_RULE_NODES];
  if (!nodes) {
    nodes = new Map(ruleGraph.nodes.map((node: any) => [node.v, node.value]));
    if (Object.isExtensible(ruleGraph)) Object.defineProperty(ruleGraph, RELEASE_RULE_NODES, { value: nodes });
  }
  return { ruleNode: (guid) => nodes.get(guid), documents: release.documents };
};


/** @deprecated name: use DecisionGraphRelease */
export type LegacyGraphRelease = DecisionGraphRelease;
/** @deprecated name: use decisionGraphRelease */
export const legacyGraphRelease = (release: { rule_graph?: any; documents?: any } | undefined) => decisionGraphRelease(release);
